import { BrandProp } from "@mojo/types";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import React from "react";
import useMeasure from "../utils/useMeasure";
import {
  extraPaddingLeftVar,
  extraPaddingRightVar,
  VanillaInput,
  VanillaInputContainer,
  VanillaInputIcons,
} from "./styles/base.css";
import { ConfusedInput } from "./styles/Confused.css";
import { MojoInput, MojoInputIconLeft, MojoInputIconRight } from "./styles/mojo.css";
import { MoneyInput, MoneyInputIconLeft, MoneyInputIconRight } from "./styles/money.css";
import { UswitchInput } from "./styles/uswitch.css";
import { ZooplaInput } from "./styles/zoopla.css";

export type InputProps =
  & React.InputHTMLAttributes<HTMLInputElement>
  & BrandProp
  & {
    /**
     * Display an icon (as a react component or string) left of the input
     */
    leftIcon?: React.ReactNode | string;
    /**
     * Display an icon (as a react component or string) right of the input
     */
    rightIcon?: React.ReactNode | string;
  };

// eslint-disable-next-line react/display-name
const Input = React.forwardRef<
  HTMLInputElement & {
    Masked: JSX.Element;
  },
  InputProps
>(({ brand = "Mojo", leftIcon, rightIcon, ...props }: InputProps, ref: any) => {
  const [leftIconRef, { width: leftIconWidth }] = useMeasure();
  const [rightIconRef, { width: rightIconWidth }] = useMeasure();

  return (
    <div
      className={`${brand} ${VanillaInputContainer({ brand })}`}
      style={assignInlineVars({
        [extraPaddingRightVar]: `${rightIconWidth}px`,
        [extraPaddingLeftVar]: `${leftIconWidth}px`,
      })}
    >
      <input
        {...props}
        className={`
          ${VanillaInput}
          ${props.className || ""}
          ${MojoInput}
          ${UswitchInput}
          ${MoneyInput}
          ${ConfusedInput}
          ${ZooplaInput}
        `}
        ref={ref}
      />
      {leftIcon && (
        <div
          className={`
            input__leftIcon
            ${VanillaInputIcons}
            ${MojoInputIconLeft}
            ${MoneyInputIconLeft}
          `}
          style={{
            left: -1,
          }}
          ref={leftIconRef}
        >
          {leftIcon}
        </div>
      )}
      {rightIcon && (
        <div
          className={`
            input__rightIcon
            ${VanillaInputIcons}
            ${MojoInputIconRight}
            ${MoneyInputIconRight}
          `}
          style={{
            right: -1,
          }}
          ref={rightIconRef}
        >
          {rightIcon}
        </div>
      )}
    </div>
  );
});

let InputNamespace = Input;

export default InputNamespace;

// This is to avoid circular dependency issues. Can't import Masked at the top because Masked imports Input.
import("./Masked").then(MaskedModule => {
  Object.assign(InputNamespace, { Masked: MaskedModule.default });
});
